import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { Store } from '@ngrx/store';

import * as fromStore from '../../store';

import { getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { EventCardComponent } from '../../components/event-card/event-card.component';
import { HeaderComponent } from '../../../standalone/components/header/header.component';
import { DictionaryNgrxModule, FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { IconComponent } from '@teamfoster/sdk/icon';

import { NgClass } from '@angular/common';
import { EventCardSliderComponent } from '../../components/event-card-slider/event-card-slider.component';
import { EventFiltersComponent } from '../../components/event-filters/event-filters.component';
import { RouterLink } from '@angular/router';
import { addDays, addWeeks, endOfDay, endOfMonth, endOfWeek, startOfMonth, startOfWeek } from 'date-fns';
import { trigger } from '@angular/animations';
import { CardGridAnimationOverview } from 'src/app/animations';
import { ImageSharpConfig, ResizePipe } from '@teamfoster/sdk/image';
import { SetCanonical, SetTags, SetTitle } from 'src/app/store';
import { StringUtility } from '@teamfoster/sdk/text-utility';
import { LoadingIndicatorComponent } from 'src/app/standalone/components/loading-indicator/loading-indicator.component';
import { InViewportDirective } from '@teamfoster/sdk/in-view';
import { environment } from 'src/environments/environment.prod';

@Component({
  selector: 'app-event-overview',
  templateUrl: './event-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styleUrls: ['./event-overview.component.scss'],
  imports: [
    EventCardComponent,
    HeaderComponent,
    DictionaryNgrxModule,
    IconComponent,
    EventCardSliderComponent,
    NgClass,
    EventFiltersComponent,
    RouterLink,
    LoadingIndicatorComponent,
    InViewportDirective,
  ],
  animations: [trigger('CardGridAnimation', [CardGridAnimationOverview])],
})
export class EventOverviewComponent implements OnInit {
  private store = inject(Store);
  lang = inject(LanguageService);
  private dict = new FromDictionaryPipe(this.lang);

  // Filters from queryparams
  date = input<string>('');
  from = input<string>('');
  to = input<string>('');
  eventType = input<string[]>([]);
  categories = input<string[]>([]);
  ageRequirements = input<string[]>([]);
  accessibilityFeatures = input<string[]>([]);
  discounts = input<string[]>([]);
  themes = input<string[]>([]);
  locations = input<string[]>([]);
  departments = input<string[]>([]);

  cookiePreferences$ = this.store.selectSignal(getCookiePreferences);
  events$ = this.store.selectSignal(fromStore.getAllEvents);
  loading$ = this.store.selectSignal(fromStore.getEventsOverviewLoading);
  eventsPage$ = this.store.selectSignal(fromStore.getEventsPage);
  featured$ = this.store.selectSignal(fromStore.getAllFeaturedEvents);
  filterFields$ = this.store.selectSignal(fromStore.getFilterFields);

  filterInput = computed(() => {
    const queryParams: { [key: string]: string | string[] | any } = {
      from: this.from(),
      to: this.to(),
      date: this.date(),
      eventType: this.eventType(),
      categories: this.categories(),
      ageRequirements: this.ageRequirements(),
      accessibilityFeatures: this.accessibilityFeatures(),
      discounts: this.discounts(),
      themes: this.themes(),
      locations: this.locations(),
      departments: this.departments(),
    };

    // remove empty and create array for single values
    return Object.keys(queryParams)
      .filter(a => queryParams[a])
      .reduce((acc: { [key: string]: string[] | string }, key) => {
        acc[key] = (Array.isArray(queryParams[key]) ? queryParams[key] : [queryParams[key]]) as string[];
        return acc;
      }, {});
  });

  filterValues$: WritableSignal<{ [key: string]: string[] | string | undefined }> = signal({});

  // start mock data ---------
  options: { name: string; id: string }[] = Array.from({ length: 10 }, (_, i) => ({
    name: `Option ${i + 1}`,
    id: `${i + 1}`,
  }));
  filterOptions: {
    key: string;
    dict?: string;
    options: { name: string; id: number | string; hint?: string; dateRange?: { from?: Date; to?: Date } }[];
  }[] = [
    {
      key: 'date',
      dict: 'datum',
      options: [
        { name: 'Vandaag', id: 'today', dateRange: { from: new Date(), to: endOfDay(new Date()) } },
        { name: 'Morgen', id: 'tomorrow', dateRange: { from: addDays(new Date(), 1) } },
        { name: 'Deze week', id: 'thisWeek', dateRange: { from: startOfWeek(new Date()), to: endOfWeek(new Date()) } },
        {
          name: 'Volgende week',
          id: 'nextWeek',
          dateRange: { from: startOfWeek(addWeeks(new Date(), 1)), to: endOfWeek(addWeeks(new Date(), 1)) },
        },
        { name: 'Deze maand', id: 'thisMonth', dateRange: { from: startOfMonth(new Date()), to: endOfMonth(new Date()) } },
        { name: 'Verleden', id: 'archive' },
      ],
    },
    {
      key: 'ageRequirements',
      dict: 'leeftijdsgroepen',
      options: [...this.filterFields$()['ageRequirements']],
    },
    {
      key: 'categories',
      dict: 'categorieen',
      options: [...this.filterFields$()['categories']],
    },
    {
      key: 'eventType',
      dict: 'types',
      options: [...this.filterFields$()['eventTypes']],
    },
    // {
    //   key: 'discounts',
    //   dict: 'kortingen',
    //   options: [...this.filterFields$()['discounts']],
    // },
    {
      key: 'themes',
      dict: 'themas',
      options: [...this.filterFields$()['themes']],
    },
    {
      key: 'departments',
      dict: 'Afdelingen',
      options: [...this.filterFields$()['departments']],
    },
    {
      key: 'locations',
      dict: 'locaties',
      options: [...this.filterFields$()['locations']],
    },
    {
      key: 'accessibilityFeatures',
      dict: 'toegankelijkheid',
      options: [...this.filterFields$()['accessibility']],
    },
  ];
  // end mock data ---------------------

  cd = inject(ChangeDetectorRef);

  sidebarItems$ = computed(() => {
    //  return exampleEvents;
    return [];
  });

  loadMoreEvents() {
    this.store.dispatch(fromStore.LoadMoreEvents());
  }

  ngOnInit(): void {
    this.setMetaData();
  }

  setMetaData() {
    const title = this.dict.transform('agenda-overzicht-meta-titel', false) || this.dict.transform('agenda-overzicht-titel');
    const description = this.dict.transform('agenda-overzicht-meta-beschrijving');
    const image = this.dict.transform('agenda-overzicht-meta-afbeelding-url', false) || this.dict.transform('meta-afbeelding-url', false);
    const resizeImage = new ResizePipe(ImageSharpConfig);

    const meta: { [key: string]: string } = {
      'description': StringUtility.Truncate(description, 157, '...'),
      'og:title': `${title} ${this.dict.transform('meta-titel-suffix')}`,
      'og:description': StringUtility.Truncate(description, 157, '...'),
    };

    if (image) {
      meta['og:image'] = 'https:' + resizeImage.transform(image, 1200, 630, 'middlecenter');
    }

    this.store.dispatch(
      SetTitle({
        title: `${title} ${this.dict.transform('meta-titel-suffix') ? '- ' + this.dict.transform('meta-titel-suffix') : ''}`,
      })
    );
    this.store.dispatch(SetCanonical({ url: `${environment.canonicalDomain}/agenda` }));
    this.store.dispatch(
      SetTags({
        properties: meta,
      })
    );
  }

  //handleFilterChange(e: { [key: string]: string[] }) {
  //  this.filterValues$.set(e);
  //  this.cd.detectChanges();
  //}
}
